import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const env = String(process.env.REACT_APP_ENV || "").toLowerCase();
const isProd = env === "prod" || env === "production";

export const defaultFlags = {
  appConfigReady: false,
  privateCapsuleShareFlowEnabled: !isProd,
  otpRequiredForCoreFlows: isProd,
  privateCapsuleMultiMediaEnabled: !isProd,
  userSearchEnabled: !isProd,
  uncoverSeedingEnabled: true,
  geminiFindEnabled: false,
  firstRunOnboardingEnabled: false,
  firstRunOnboardingShowEveryTime: false,
  firstRunOnboardingMode: "once",
};

function boolOrDefault(value, fallback) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "n", "off"].includes(normalized)) return false;
  }
  return fallback;
}

function onboardingModeOrDefault(value, fallback = defaultFlags.firstRunOnboardingMode) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "always" || normalized === "everytime" || normalized === "every_time") {
    return "always";
  }
  if (normalized === "once" || normalized === "first" || normalized === "first_time") {
    return "once";
  }
  return fallback;
}

export function normalizeAppConfigFlags(data = {}) {
  const firstRunOnboardingShowEveryTime = boolOrDefault(
    data.firstRunOnboardingShowEveryTime,
    defaultFlags.firstRunOnboardingShowEveryTime
  );
  const firstRunOnboardingMode = Object.prototype.hasOwnProperty.call(
    data,
    "firstRunOnboardingMode"
  )
    ? onboardingModeOrDefault(
        data.firstRunOnboardingMode,
        defaultFlags.firstRunOnboardingMode
      )
    : firstRunOnboardingShowEveryTime
      ? "always"
      : "once";

  return {
    appConfigReady: true,
    privateCapsuleShareFlowEnabled: boolOrDefault(
      data.privateCapsuleShareFlowEnabled,
      defaultFlags.privateCapsuleShareFlowEnabled
    ),
    otpRequiredForCoreFlows: boolOrDefault(
      data.otpRequiredForCoreFlows,
      defaultFlags.otpRequiredForCoreFlows
    ),
    privateCapsuleMultiMediaEnabled: boolOrDefault(
      data.privateCapsuleMultiMediaEnabled,
      defaultFlags.privateCapsuleMultiMediaEnabled
    ),
    userSearchEnabled: boolOrDefault(data.userSearchEnabled, defaultFlags.userSearchEnabled),
    uncoverSeedingEnabled: boolOrDefault(
      data.uncoverSeedingEnabled,
      defaultFlags.uncoverSeedingEnabled
    ),
    geminiFindEnabled: boolOrDefault(
      data.geminiFindEnabled,
      defaultFlags.geminiFindEnabled
    ),
    firstRunOnboardingEnabled: boolOrDefault(
      data.firstRunOnboardingEnabled,
      defaultFlags.firstRunOnboardingEnabled
    ),
    firstRunOnboardingShowEveryTime,
    firstRunOnboardingMode,
  };
}

const APP_CONFIG_CACHE_KEY = "inssa:app-config-flags:v1";
// Never block first paint for longer than this behind a slow config read.
const CONFIG_READY_TIMEOUT_MS = 2500;

function readCachedFlags() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage?.getItem(APP_CONFIG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
}

function writeCachedFlags(flags) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(APP_CONFIG_CACHE_KEY, JSON.stringify(flags));
  } catch (error) {
    // Ignore quota/availability errors; caching is a best-effort optimization.
  }
}

export default function useAppConfigFlags() {
  const [flags, setFlags] = useState(() => {
    // Boot instantly from last-known flags so returning users never see the
    // gating loading screen; the live snapshot below still revalidates.
    const cached = readCachedFlags();
    return cached ? { ...defaultFlags, ...cached, appConfigReady: true } : defaultFlags;
  });

  useEffect(() => {
    let settled = false;
    const markReadyWithDefaults = () =>
      setFlags((prev) => (prev.appConfigReady ? prev : { ...defaultFlags, appConfigReady: true }));

    const ref = doc(db, "config", "app");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        settled = true;
        const data = snap.exists() ? snap.data() || {} : {};
        const next = normalizeAppConfigFlags(data);
        setFlags(next);
        writeCachedFlags(next);
      },
      () => {
        settled = true;
        markReadyWithDefaults();
      }
    );

    // Fail open: if the config read is slow and we have no cached flags,
    // proceed with defaults rather than holding the route on a blank screen.
    const timeoutId = setTimeout(() => {
      if (!settled) markReadyWithDefaults();
    }, CONFIG_READY_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
      unsub();
    };
  }, []);

  return flags;
}
